import { FundOwnershipCapTableDetailsModel, ProFormaFundOwnershipDetailModel } from 'api';
import { getObjectValue } from 'utilities';
import { CalculateOwnersSharesValueParams } from '../types';
import getOwnerShareValue from './getOwnerShareValue';
import getUniqueSecurityAndOwnerCombination from './getUniqueSecurityAndOwnerCombination';
import { WeightedShareValue } from '../components/FundFirmDistribution/FundFirmDistribution.props';

const calculateOwnersSharesValue = ({
  sortedPresentValues,
  capTableDetail,
  fundList,
  childrenCapTables,
  childrenCompanies,
  mainFundOwnershipRef,
  entityReferenceId = null,
}: CalculateOwnersSharesValueParams) => {
  const { securities, id: currentCapTableId } = getObjectValue(capTableDetail);
  if (!securities) {
    return [];
  }
  const { fund_ownership_detail: fundOwnershipDetail } =
    mainFundOwnershipRef.current as FundOwnershipCapTableDetailsModel;

  const fundFilterCallback = (fod: ProFormaFundOwnershipDetailModel) =>
    entityReferenceId ? fod.fund && fod.entity_reference === entityReferenceId : fod.fund;
  const uniqueFundIds = new Set(fundOwnershipDetail.filter(fundFilterCallback).map(fod => fod.fund));
  const uniqueRelatedEntitiesIds = new Set(fundOwnershipDetail.map(fod => fod.related_entity));
  // boolean to hide related entities tables if waterfall is running for a related entity cap table
  const isRelatedEntityCapTable = childrenCapTables.some(({ id }) => id === currentCapTableId);
  // fundList are all the funds in the firm, so we do some additional filtering
  const fundsWithOwnership = fundList?.filter(fund => uniqueFundIds.has(fund.id));
  const relatedEntitiesWithOwnership = isRelatedEntityCapTable
    ? []
    : (childrenCompanies ?? []).filter(relatedEntity => uniqueRelatedEntitiesIds.has(Number(relatedEntity.company_id)));
  const uniqueSecurityAndFundCombination = getUniqueSecurityAndOwnerCombination({
    fundOwnershipDetail,
    owner: 'fund',
  });
  const uniqueSecurityAndRelatedEntityCombination = getUniqueSecurityAndOwnerCombination({
    fundOwnershipDetail,
    owner: 'related_entity',
  });
  const uniqueSecurityAndOwnerCombination = [
    ...uniqueSecurityAndFundCombination,
    ...uniqueSecurityAndRelatedEntityCombination,
  ];

  const fundsShares = (fundsWithOwnership ?? []).map(fo => ({
    fund: { id: fo.id, name: fo.name },
    owner: 'fund',
    name: `${fo.name} Shares`,
    share_values: uniqueSecurityAndOwnerCombination
      .filter((fod: ProFormaFundOwnershipDetailModel) => fod.fund === fo.id)
      .map((item: ProFormaFundOwnershipDetailModel) =>
        getOwnerShareValue({
          fod: item,
          securities,
          fundOwnershipDetails: fundOwnershipDetail as ProFormaFundOwnershipDetailModel[],
          sortedPresentValues,
          owner: 'fund',
          ownerId: fo.id as number,
        })
      ),
  })) as unknown as WeightedShareValue[];
  const relatedEntitiesShares = relatedEntitiesWithOwnership.map(re => ({
    fund: { id: Number(re.company_id), name: re.company_name },
    owner: 'related_entity',
    name: `${re.company_name} Shares`,
    share_values: uniqueSecurityAndOwnerCombination
      .filter((fod: ProFormaFundOwnershipDetailModel) => fod.related_entity === Number(re.company_id))
      .map((item: ProFormaFundOwnershipDetailModel) =>
        getOwnerShareValue({
          fod: item,
          securities,
          fundOwnershipDetails: fundOwnershipDetail as ProFormaFundOwnershipDetailModel[],
          sortedPresentValues,
          owner: 'related_entity',
          ownerId: Number(re.company_id),
        })
      ),
  })) as unknown as WeightedShareValue[];
  return [...fundsShares, ...relatedEntitiesShares];
};

export default calculateOwnersSharesValue;
